/**
 * DroppedItem - An item dropped in the world that can be picked up
 */

import { Vector3 } from '../math/Vector3';
import { Renderer } from '../core/Renderer';
import { Item } from '../items/Item';
import { Entity } from './Entity';
import { Localization } from '../systems/Localization';

export class DroppedItem extends Entity {
  readonly item: Item;
  readonly quantity: number;
  private bobOffset: number = 0;
  private bobTime: number = 0;
  private lifetime: number = 0;
  private readonly maxLifetime: number = 120; // 2 minutes
  private readonly pickupDelay: number = 0.5; // Small delay before can be picked up
  private readonly pickupRange: number = 1.5;

  constructor(position: Vector3, item: Item, quantity: number = 1) {
    super(position);
    this.item = item;
    this.quantity = quantity;
    // Random starting bob phase
    this.bobTime = Math.random() * Math.PI * 2;
  }

  update(deltaTime: number): void {
    super.update(deltaTime);

    // Bob animation
    this.bobTime += deltaTime * 3;
    this.bobOffset = Math.sin(this.bobTime) * 0.1;

    // Track lifetime
    this.lifetime += deltaTime;
    if (this.lifetime > this.maxLifetime) {
      this.isActive = false;
    }
  }

  /** Check if this item can be picked up */
  canPickup(): boolean {
    return this.lifetime > this.pickupDelay;
  }

  /** Get the pickup range */
  getPickupRange(): number {
    return this.pickupRange;
  }

  /** Check if player is in pickup range */
  isInRange(playerPos: Vector3): boolean {
    const dx = this.position.x - playerPos.x;
    const dy = this.position.y - playerPos.y;
    return Math.sqrt(dx * dx + dy * dy) <= this.pickupRange;
  }

  render(renderer: Renderer): void {
    if (!this.isActive) return;

    const ctx = renderer.getContext();
    const screenPos = renderer.worldToScreen(
      new Vector3(this.position.x, this.position.y, this.bobOffset + 0.3)
    );

    // Glow effect
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y, 0,
      screenPos.x, screenPos.y, 20
    );
    gradient.addColorStop(0, 'rgba(255, 255, 200, 0.4)');
    gradient.addColorStop(1, 'rgba(255, 255, 200, 0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y, 20, 0, Math.PI * 2);
    ctx.fill();

    // Item representation (colored square based on item type)
    const size = 16;
    const color = this.getItemColor();

    // Shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(screenPos.x - size/2 + 2, screenPos.y - size/2 + 2, size, size);

    // Item box
    ctx.fillStyle = color;
    ctx.fillRect(screenPos.x - size/2, screenPos.y - size/2, size, size);

    // Border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.strokeRect(screenPos.x - size/2, screenPos.y - size/2, size, size);

    // Quantity indicator if > 1
    if (this.quantity > 1) {
      ctx.fillStyle = '#fff';
      ctx.font = '10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`x${this.quantity}`, screenPos.x, screenPos.y + size/2 + 12);
    }

    // Item name on hover (always show for now) - localized
    const itemInfo = Localization.getItem(this.item.id);
    const itemName = itemInfo?.name || this.item.name;
    ctx.fillStyle = '#fff';
    ctx.font = '11px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(itemName, screenPos.x, screenPos.y - size/2 - 5);
  }

  private getItemColor(): string {
    switch (this.item.type) {
      case 'WEAPON':
        return '#e74c3c'; // Red for weapons
      case 'TOOL':
        return '#3498db'; // Blue for tools
      case 'CONSUMABLE':
        return '#2ecc71'; // Green for consumables
      case 'RESOURCE':
        return '#f39c12'; // Orange for resources
      default:
        return '#9b59b6'; // Purple for other
    }
  }
}
